import { useState, useContext } from 'react';
import HttpClient, { HttpClientResponse } from './HttpClient';
import { AppContext, AppContextType } from 'context/AppContext';
import { MappedEntityType, Entity, resolvePath } from 'services/resolvers';
import { openSnackbar } from 'berry/store/slices/snackbar';
import { Store } from 'berry/store';
import resolveData from 'services/resolvers/Mappings/DataResolvers';
import getDataSource from 'services/resolvers/Mappings/SourceResolver';

const SESSION_EXPIRED = 'Session expired, please login again';

export interface HttpResponse<T> {
    data: T | any;
    error: Error | unknown | null;
    loading: boolean;
}

export type DataSource = 'hyrerocket' | 'micro-v3';

export type RequestProps = {
    path?: string;
    queryParams?: string[];
    body?: any;
};

type RequestConfig<T> = {
    dataSource: DataSource;
    method: keyof HttpClient<T>;
    responseType: T;
    mappedType: MappedEntityType;
};

function getDataSourceUrl(dataSource: DataSource) {
    switch (dataSource) {
        case 'hyrerocket':
            return process.env.REACT_APP_HYREROCKET_API_URL || 'https://api.hyrerocket.com/api/v2';
        case 'micro-v3':
            return process.env.REACT_APP_MICRO_V3_API_URL || 'http://apidev.teamstation.dev/api/v3';
        default:
            return '';
    }
}

export type DetachedRequest<T> = (requestProps: RequestProps) => Promise<T | null>;

export type AgHttpClient<T> = {
    response: HttpResponse<T>;
    request: (requestProps: RequestProps) => void;
    asyncDetachedRequest: DetachedRequest<T>;
    triggerAlert: (success: boolean, message?: string) => void;
};

const { useDispatch } = Store;

function useHttpClient<T>(
    entity: Entity,
    mappedType: MappedEntityType,
    defaultData: any = null,
    successText?: string,
    errorText?: string
): AgHttpClient<T> {
    const dispatch = useDispatch();
    const [response, setResponse] = useState<HttpResponse<T>>({
        data: defaultData,
        error: null,
        loading: false
    });

    const triggerAlert = (success: boolean, message?: string) => {
        if (!message) return;
        console.log('message', message);
        dispatch(
            openSnackbar({
                open: true,
                message,
                variant: 'alert',
                anchorOrigin: { vertical: 'top', horizontal: 'right' },
                alert: {
                    color: success ? 'primary' : 'error'
                }
            })
        );
    };

    const { appToken, setAppToken } = useContext(AppContext) as AppContextType;

    const sendRequest = async (requestFunc: () => Promise<HttpClientResponse<T>>, { dataSource }: RequestConfig<T>) => {
        setResponse((prevState) => ({ ...prevState, loading: true }));

        try {
            const httpClientResponse = await requestFunc();

            if (httpClientResponse.status === 403) {
                triggerAlert(false, SESSION_EXPIRED);
                setAppToken(undefined);
            }

            if ([200, 201, 202].find((code) => code === httpClientResponse.status)) {
                const parsedValue = resolveData<T>(dataSource, mappedType, httpClientResponse.data, httpClientResponse);

                triggerAlert(true, successText);
                setResponse({
                    data: parsedValue,
                    error: null,
                    loading: false
                });
                return;
            }

            triggerAlert(false, errorText);
            setResponse({
                data: null,
                error: new Error(`Returned status code ${httpClientResponse.status}`),
                loading: false
            });
        } catch (error) {
            triggerAlert(false, errorText);
            setResponse({
                data: null,
                error,
                loading: false
            });
        }
    };

    const getClient = (dataSource: DataSource) => {
        const dataSourceUrl = getDataSourceUrl(dataSource);
        let authHeaders: Record<string, string> = {
            Authorization: `Bearer ${appToken}` // Use the auth token from the context
        };

        if (mappedType === MappedEntityType.LoginResponse) {
            authHeaders = {};
        }

        return new HttpClient<T>(dataSourceUrl, authHeaders);
    };

    const asyncDetachedRequest = async ({ path = '', queryParams = [], body = undefined }: RequestProps): Promise<T | null> => {
        const dataSource = getDataSource(mappedType);
        const httpClient = getClient(dataSource);
        const url = resolvePath(dataSource, entity, mappedType);
        const queryParamsUrl = queryParams.length !== 0 ? `?${queryParams.join('&')}` : '';
        const parsedPath = `${url.url}${path}${queryParamsUrl}`;
        const requestFunc = httpClient[url.method].bind(httpClient, parsedPath, body);

        try {
            const httpClientResponse = await requestFunc();

            if (httpClientResponse.status === 403) {
                triggerAlert(false, SESSION_EXPIRED);
                setAppToken(undefined);
            }

            if ([200, 201, 202].find((code) => code === httpClientResponse.status)) {
                const parsedValue = resolveData<T>(dataSource, mappedType, httpClientResponse.data, httpClientResponse);
                triggerAlert(true, successText);

                return parsedValue;
            }

            triggerAlert(false, errorText);
            return null;
        } catch (error) {
            triggerAlert(false, errorText);
            return null;
        }
    };

    return {
        response,
        request: ({ path = '', queryParams = [], body = undefined }: RequestProps) => {
            const dataSource = getDataSource(mappedType);
            const httpClient = getClient(dataSource);
            const url = resolvePath(dataSource, entity, mappedType);
            const parsedPath = `${url.url}${path}?${queryParams.join('&')}`;
            const requestFunc = httpClient[url.method].bind(httpClient, parsedPath, body);
            sendRequest(requestFunc, { dataSource, method: url.method, responseType: {} as T, mappedType });
        },
        asyncDetachedRequest,
        triggerAlert
    };
}

export default useHttpClient;
